import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getConnectorLogo } from '../utils/connectorLogos';

const GitHubPlanBindingModal = ({ isOpen, onClose, planId, planName }) => {
  const [github, setGitHub] = useState(null);
  const [repositories, setRepositories] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [wasJustConnected, setWasJustConnected] = useState(false);
  const [initialBindingCount, setInitialBindingCount] = useState(0);

  const load = useCallback(async () => {
    if (!isOpen || !planId) return;
    setLoading(true);
    setError('');
    try {
      const connectorsRes = await fetchWithAuth('/api/connectors');
      const connectorsData = await connectorsRes.json();
      const githubConnector = connectorsData.connectors?.find(c => c.id === 'github') || null;
      setWasJustConnected(previous => previous || (github?.status !== 'connected' && githubConnector?.status === 'connected'));
      setGitHub(githubConnector);
      if (githubConnector?.status !== 'connected') {
        setRepositories([]);
        setSelectedIds(new Set());
        setInitialBindingCount(0);
        return;
      }

      const [reposRes, bindingsRes] = await Promise.all([
        fetchWithAuth('/api/connectors/github/repositories'),
        fetchWithAuth(`/api/connectors/plans/${planId}/bindings`),
      ]);
      const reposData = await reposRes.json();
      const bindingsData = await bindingsRes.json();
      if (!reposRes.ok) throw new Error(reposData.error || 'Failed to load repositories');
      if (!bindingsRes.ok) throw new Error(bindingsData.error || 'Failed to load plan connections');

      const repoRows = Array.isArray(reposData.repositories) ? reposData.repositories : [];
      const githubBindings = (bindingsData.bindings || []).filter(b => b.connector_id === 'github');
      setRepositories(repoRows);
      setSelectedIds(new Set(githubBindings.map(binding => String(binding.external_id))));
      setInitialBindingCount(githubBindings.length);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isOpen, planId, github?.status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [isOpen, load]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return repositories;
    return repositories.filter(repo =>
      repo.full_name.toLowerCase().includes(normalized) ||
      repo.description?.toLowerCase().includes(normalized)
    );
  }, [repositories, query]);

  const toggle = (repoId) => {
    setSelectedIds(current => {
      if (current.has(repoId)) return new Set();
      return new Set([repoId]);
    });
  };

  const connect = () => {
    const token = localStorage.getItem('token');
    const apiBase = window.location.port === '3000' ? 'http://localhost:3001' : '';
    window.open(
      `${apiBase}/api/connectors/connect/github?token=${encodeURIComponent(token || '')}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const wasPreviouslyUnbound = initialBindingCount === 0;
      const selectedRepositories = repositories.filter(repo => selectedIds.has(String(repo.id)));
      const response = await fetchWithAuth(
        `/api/connectors/plans/${planId}/github-bindings`,
        { method: 'PUT', body: { repositories: selectedRepositories } },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save repositories');
      window.dispatchEvent(new CustomEvent('pabs:githubBindingsUpdated', {
        detail: { planId, count: selectedRepositories.length },
      }));
      onClose();
      if (wasPreviouslyUnbound && selectedRepositories.length > 0) {
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent('pabs:prefillPlanChat', {
            detail: {
              planId,
              message: 'I already connected my GitHub repository, can you check it out?',
            },
          }));
        }, 150);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const logo = getConnectorLogo('github');
  const isConnected = github?.status === 'connected';
  const isDisabled = github?.disabled;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px">
        <ModalHeader>
          <HStack spacing={3}>
            <Box w="34px" h="34px" borderRadius="9px" bg={logo?.bg || '#181717'}
              display="flex" alignItems="center" justifyContent="center">
              {logo && <logo.Logo size={18} />}
            </Box>
            <Box>
              <Text fontSize="md">GitHub execution evidence</Text>
              <Text fontSize="xs" fontWeight="normal" color="gray.500" mt={0.5}>
                Choose the repository that belongs to {planName || 'this plan'}.
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          {error && <Alert status="error" borderRadius="lg" mb={4}><AlertIcon />{error}</Alert>}
          {loading ? (
            <Box py={10} textAlign="center"><Spinner size="sm" color="gray.400" /></Box>
          ) : !isConnected ? (
            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={5}>
              <Text fontWeight="600" mb={2}>Connect GitHub first</Text>
              <Text fontSize="sm" color="gray.600" lineHeight="1.6" mb={4}>
                GitHub is connected once at account level. You then choose which repositories provide evidence for each plan.
              </Text>
              <Button size="sm" colorScheme="brand" onClick={connect} isDisabled={isDisabled}>
                {isDisabled ? 'GitHub app configuration required' : 'Connect GitHub'}
              </Button>
            </Box>
          ) : (
            <>
              <Alert status={selectedIds.size === 0 ? "warning" : "info"} borderRadius="lg" mb={4}>
                <AlertIcon />
                {selectedIds.size === 0
                  ? initialBindingCount > 0
                    ? 'Saving will remove GitHub delivery evidence from this plan. You can select a repository again at any time.'
                    : wasJustConnected
                    ? 'GitHub is connected. Finish setup by selecting a repository for this plan, then save.'
                    : 'GitHub is connected, but this plan has no repository yet. Select one and save to let Pabs inspect delivery evidence.'
                  : 'This plan uses the selected repository as read-only delivery evidence.'}
              </Alert>
              <Input
                size="sm"
                placeholder="Search repositories"
                value={query}
                onChange={event => setQuery(event.target.value)}
                mb={3}
              />
              <VStack align="stretch" spacing={0} border="1px solid" borderColor="gray.200"
                borderRadius="xl" maxH="360px" overflowY="auto">
                {filtered.map((repo, index) => (
                  <HStack key={repo.id} px={4} py={3} align="flex-start"
                    borderBottom={index < filtered.length - 1 ? '1px solid' : 'none'}
                    borderColor="gray.100">
                    <Switch
                      mt={1}
                      isChecked={selectedIds.has(String(repo.id))}
                      onChange={() => toggle(String(repo.id))}
                      colorScheme="green"
                      size="sm"
                    />
                    <Box flex="1" minW={0}>
                      <HStack>
                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>{repo.full_name}</Text>
                        {repo.private && <Badge fontSize="9px">Private</Badge>}
                      </HStack>
                      {repo.description && (
                        <Text fontSize="xs" color="gray.500" noOfLines={2} mt={0.5}>{repo.description}</Text>
                      )}
                      <Text fontSize="10px" color="gray.400" mt={1}>Default branch: {repo.default_branch}</Text>
                    </Box>
                  </HStack>
                ))}
                {filtered.length === 0 && (
                  <Text fontSize="sm" color="gray.500" px={4} py={6}>No repositories found.</Text>
                )}
              </VStack>
            </>
          )}
        </ModalBody>
        {isConnected && (
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button
              colorScheme="brand"
              onClick={save}
              isLoading={saving}
              isDisabled={selectedIds.size === 0 && initialBindingCount === 0}
            >
              Save {selectedIds.size} {selectedIds.size === 1 ? 'repository' : 'repositories'}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default GitHubPlanBindingModal;
